import clsx from 'clsx';
import { useReducedMotion } from 'framer-motion';
import { isEmpty } from 'lodash-es';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Grid } from '@dynatrace/strato-components/layouts';

import { useContainerSize } from '../../../core/hooks/useContainerSize.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useSafeLayoutEffect } from '../../../core/hooks/useSafeLayoutEffect.js';
import { KeepMountedContextProvider } from '../../../core/providers/KeepMountedContextProvider.js';
import { useAnimatedPagePanels } from '../hooks/useAnimatedPagePanels.js';
import type { PanelsContextProps } from './contexts/PanelsContext.js';
import { PanelsContext } from './contexts/PanelsContext.js';
import { ParentPanelContext } from './contexts/ParentPanelContext.js';
import { panelsReducer } from './state/panels-state-reducer.js';
import type {
  PanelChildren,
  PanelSizes,
  PanelSizesWithUnit,
  ResizeHandles,
} from './types/shared-panel-types.js';
import { addPanel, dismissPanel } from './utils/conditional-rendering.js';
import {
  generateGridTemplateColumns,
  getAnimationStates,
} from './utils/layout-styles.js';
import {
  getInitialGridStylesAndSizes,
  resizePanels,
} from './utils/panel-sizes.js';
import {
  getAddedPanels,
  getRemovedPanels,
  getSortedPanelChildren,
} from './utils/panels.js';
import {
  getActualHandleIndex,
  getSortedResizeHandles,
} from './utils/resize-handles.js';
import {
  anyUnitToPx,
  convertToPanelSizes,
  fractionsToPx,
  parseValue,
  pxToFractions,
} from './utils/units.js';

/** @internal */
export interface ParentPanelProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  /** PanelName */
  name: string;
  /**
   * Whether animations should be disabled.
   * If true or 'prefers-reduced-motion' is set to 'reduce', no animations are applied.
   * @defaultValue false
   */
  disableAnimations?: boolean;
}

// TODO: When dismissing a panel, the ParentPanel re-renders multiple times, sometimes returning differing container sizes.
//        e.g.: Panels initially calculated with 1000px. Dismiss panel -> hook returns 984px -> calculate new sizes, re-render -> hook returns 1000px again.
//        So we are calculating with the wrong size. Even though the result is just slightly off, we should definitely check that. Workaround for now is to not
//        use the width we get from the useContainerSize hook directly, but instead get the container's offsetWidth in the useSafeLayoutEffect where we are
//        initializing the page and handling conditionally rendered panels.

/**
 * The `ParentPanel` holds a number of `Panel`s and `Resizer`s and handles all the calculations needed to render panels with a specific
 * preferred width, minimum width, or panels that are rendered conditionally. It also provides the functionality to resize panels.
 * @internal
 */
export const ParentPanel = (props: ParentPanelProps) => {
  const {
    name,
    disableAnimations: propDisableAnimations = false,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  }: ParentPanelProps = props;
  // State that panels and resize handles are registered to.
  const [state, dispatch] = useReducer(panelsReducer, {
    panels: {},
    resizeHandles: {},
  });

  // Styles being applied to the parent panel for sizing the panels and positioning them correctly.
  const [layoutStyles, setLayoutStyles] = useState<CSSProperties | undefined>(
    undefined,
  );
  // Currently applied sizes.
  const sizes = useRef<PanelSizesWithUnit>({});
  // Panel sizes, including pixels, used for animating page panels.
  const panelSizes = useRef<PanelSizes>({});
  const previousSizes = useRef<PanelSizesWithUnit>({});
  // Sizes that panels should be restored to when expanded.
  const restoreSizes = useRef<PanelSizesWithUnit>({});

  // Panels sorted by their order.
  const sortedPanels = useRef<PanelChildren>([]);
  // Keep track of previous panels. In case the panels change, we need to re-initialize.
  const prevPanels = useRef<PanelChildren>([]);
  useEffect(() => {
    prevPanels.current = sortedPanels.current;
  });

  // Resize handles sorted by their order.
  const sortedResizeHandles = useRef<ResizeHandles>([]);
  // Keep track of previous resize handles. Needed for animating removal of panels.
  const prevResizeHandles = useRef<ResizeHandles>([]);
  useEffect(() => {
    prevResizeHandles.current = sortedResizeHandles.current;
  });

  // Resize handle currently being dragged to differentiate click and drag actions.
  const resizingHandle = useRef<number | null>(null);

  const parentPanelRef = useRef<HTMLDivElement | null>(null);
  // In case the min width of all panels exceeds the parent panel width, only the scroll
  // width of the parent panel changes. To be able to react to scroll width changes, we
  // need to nest the observed element inside the parent panel.
  const widthMeasurementContainer = useRef<HTMLDivElement | null>(null);
  const { width: parentPanelWidth } = useContainerSize(
    widthMeasurementContainer,
  );
  const parentPanelCallbackRef = useCallback((el: HTMLDivElement) => {
    parentPanelRef.current = el;
  }, []);

  const prefersReducedMotion = useReducedMotion();
  const disableAnimations =
    propDisableAnimations || prefersReducedMotion === true;

  const { containerRef, animatePagePanels } = useAnimatedPagePanels({
    setLayoutStyles,
  });

  const rootRef = useMergeRefs([containerRef, parentPanelCallbackRef]);

  // Update the grid template columns of the panels grid to reflect the new panel sizes.
  const updateLayoutStyles = useCallback(
    (newSizes: PanelSizesWithUnit, newGridTemplateColumns?: string) => {
      const gridTemplateColumns =
        newGridTemplateColumns ??
        generateGridTemplateColumns(
          sortedPanels.current,
          sortedResizeHandles.current,
          newSizes,
        );

      setLayoutStyles((prev) => {
        // Only set the new styles if the columns actually changed to prevent
        // unnecessary re-renders.
        if (!prev || prev.gridTemplateColumns === gridTemplateColumns) {
          return prev;
        }

        previousSizes.current = sizes.current;
        sizes.current = newSizes;
        panelSizes.current = {
          ...convertToPanelSizes(
            newSizes,
            parentPanelWidth,
            sortedResizeHandles.current.length,
          ),
        };

        return {
          ...prev,
          gridTemplateColumns,
        };
      });
    },
    [parentPanelWidth],
  );

  const callResizeCallbacks = useCallback(() => {
    if (!parentPanelRef.current || isEmpty(previousSizes.current)) {
      return;
    }

    for (const panel of sortedPanels.current) {
      if (previousSizes.current[panel.name] !== sizes.current[panel.name]) {
        panel.onResize?.(
          convertToPanelSizes(
            sizes.current,
            parentPanelWidth,
            sortedResizeHandles.current.length,
          ),
          fractionsToPx(
            sizes.current[panel.name].value,
            parentPanelWidth,
            sortedResizeHandles.current.length,
          ),
        );
      }
    }
  }, [parentPanelWidth]);

  /**
   * Updates the space distribution in favor for panels below their min width. So that in case the window was resized and any of the panels
   * reached its minWidth the width in fraction is updating correctly.
   * @returns minWidth: null | number - in case the minWidth changed we return the new min width and null in case nothing changed.
   */
  const updateSpaceDistribution = () => {
    if (!parentPanelRef.current) {
      return;
    }

    const newSizes = { ...sizes.current };
    const panelsAtMinWidth: string[] = [];
    // Total amount of fractions that panels need to shrink to make space for the panels at minWidth.
    let delta = 0;

    // Check if any of the panels is at its minWidth. If so, we need to increase its size.
    for (const panel of sortedPanels.current) {
      const currentSizePx = fractionsToPx(
        sizes.current[panel.name].value,
        parentPanelWidth,
        sortedResizeHandles.current.length,
      );

      if (
        panel.minWidth &&
        currentSizePx < panel.minWidth &&
        currentSizePx !== 0
      ) {
        panelsAtMinWidth.push(panel.name);
        const targetFractions = pxToFractions(
          panel.minWidth,
          parentPanelWidth,
          sortedResizeHandles.current.length,
        );
        // The difference between minWidth in fractions and the previous size
        // needs to be subtracted from other panels.
        delta += targetFractions - sizes.current[panel.name].value;
        newSizes[panel.name].value = targetFractions;
      }
    }

    // No panel is at its minWidth yet, leave everything as is.
    if (!delta) {
      return;
    }

    // To preserve the px size of all panels, the delta needs to be subtracted
    // considering the percentage of space the panels to subtract from take up.

    // The total amount of fractions of all the panels we can shrink.
    const totalFractions = sortedPanels.current.reduce((fr, panel) => {
      if (!panelsAtMinWidth.includes(panel.name)) {
        return fr + sizes.current[panel.name].value;
      }

      return fr;
    }, 0);

    for (const panel of sortedPanels.current) {
      if (!panelsAtMinWidth.includes(panel.name)) {
        const offsetPercentage =
          (100 / totalFractions) * sizes.current[panel.name].value;
        newSizes[panel.name].value -=
          Math.round(delta * offsetPercentage) / 100;
      }
    }

    updateLayoutStyles(newSizes);
  };

  /**
   * Helper function to resize panels either via dragging or updating the props.
   * @param deltaX - amount of fractions the panel size should be in- or decreased by.
   * @param handleOrder - index of the affected resize handle.
   * @returns
   */
  const handleResize = (
    deltaX: number,
    handleOrder: number,
    currentSizes: PanelSizesWithUnit = sizes.current,
    runUpdate: boolean = true,
  ) => {
    if (!parentPanelRef.current) {
      return;
    }

    resizingHandle.current = handleOrder;
    // Due to panels being rendered conditionally, the handle order does not necessarily match
    // the index in the sorted array. We need the index to get the corresponding panels before and
    // after the resize handle.
    const handleIndex = getActualHandleIndex(sortedResizeHandles, handleOrder);

    const panelChildren = sortedPanels.current;
    const prevPanel = panelChildren[handleIndex];
    const nextPanel = panelChildren[handleIndex + 1];

    // There must be at least one panel before and one after the resize handle.
    // Else, we ignore the resize event.
    if (prevPanel && nextPanel) {
      const { gridTemplateColumns, newSizes } =
        resizePanels(
          handleIndex,
          sortedPanels.current,
          sortedResizeHandles.current,
          currentSizes,
          deltaX,
          parentPanelWidth,
        ) || {};

      if (gridTemplateColumns && newSizes && runUpdate) {
        updateLayoutStyles(newSizes, gridTemplateColumns);
      }
      return newSizes;
    }
  };

  // Listens to state changes to initialize or update the sizes of the panels and the corresponding styles
  // to apply to the root panel after all panels have been mounted.
  useSafeLayoutEffect(() => {
    const panelKeys = Object.keys(state.panels);

    if (!parentPanelRef.current) {
      return;
    }

    /*
      For animating the panels, we can't just remove / add template columns, since the transition
      only works for the same amount of columns (see https://www.w3.org/TR/css-grid-1/#propdef-grid-template-columns)
      We have to first set the columns and template areas to what we want to animate from, defer setting what we
      want to animate to the next tick to trigger the transition, and when the transition is done, set the
      grid to the final state.

      e.g.:
        panels:   panel1 panel1-resizer panel2 / 20fr 8px 80fr
        action:   add panel3 + corresponding resize handle
        from:     panel1 panel1-resizer panel2 panel3-resizer panel3 / 20fr 8px 80fr 8px 0fr
        to:       panel1 panel1-resizer panel2 panel3-resizer panel3 / 20fr 8px 60fr 8px 20fr
        final:    panel1 panel1-resizer panel2 panel3-resizer panel3 / 20fr 8px 60fr 8px 20fr

        panels:   panel1 panel1-resizer panel2 / 20fr 8px 80fr
        action:   remove panel1 + corresponding resize handle
        from:     panel1 panel1-resizer panel2 / 20fr 8px 80fr
        to:       panel1 panel1-resizer panel2 / 0fr 0px 100fr
        final:    panel2 / 100fr
    */

    let newSizesAnimationStart: PanelSizesWithUnit | undefined;
    let newSizesAnimationEnd: PanelSizesWithUnit | undefined;
    let newSizesFinal: PanelSizesWithUnit | undefined;

    const panelChildren = getSortedPanelChildren(state.panels);
    const parentPanelWidth = parentPanelRef.current.scrollWidth;
    sortedPanels.current = panelChildren;
    const resizeHandles = getSortedResizeHandles(state.resizeHandles);
    sortedResizeHandles.current = resizeHandles;

    // Nothing to initialize.
    if (!panelChildren.length && !resizeHandles.length) {
      return;
    }

    const removedPanels = getRemovedPanels(prevPanels.current, state);

    // Get panels that have been added to the state.
    // Used for re-applying the restore size if the panel has already been rendered before.
    const addedPanels = getAddedPanels(panelKeys, sizes.current, name);

    if (removedPanels.length === 0 && addedPanels.length === 0) {
      return;
    }

    // If a panel has been removed or added and we already initialized the page before,
    // we don't want to re-initialize again, but instead adjust the existing panels to
    // re-distribute the space.
    let handledPanels = [...prevPanels.current];
    // Panel has been dismissed.
    if (removedPanels.length) {
      // If multiple panels are dismissed at the same time, we are removing them one-by-one. Hence, we can't use the
      // current sortedPanels directly, but need to use the previous panels and remove the new panels one-by-one.
      for (const removedPanel of removedPanels) {
        // Add current panel size of removed panels to the restore sizes so we can
        // apply the previous size again if the panel is added back in.
        restoreSizes.current[removedPanel] = sizes.current[removedPanel];
        [newSizesFinal, newSizesAnimationStart, newSizesAnimationEnd] =
          dismissPanel(
            removedPanel,
            newSizesFinal ?? sizes.current,
            newSizesAnimationStart ?? sizes.current,
            newSizesAnimationEnd ?? sizes.current,
            handledPanels,
            sortedResizeHandles.current,
            parentPanelWidth,
          );
        handledPanels = handledPanels.filter(
          (panel) => panel.name !== removedPanel,
        );
      }
    }

    // Panel has been added.
    if (!isEmpty(sizes.current) && addedPanels.length) {
      // If multiple panels are added at the same time, we are adding them one-by-one. Hence, we can't use the
      // current sortedPanels directly, but need to use the previous panels and push the new panels one-by-one.
      for (const addedPanel of addedPanels) {
        handledPanels.push({ name: addedPanel, ...state.panels[addedPanel] });
        handledPanels.sort((a, b) => (a.order < b.order ? -1 : 1));

        [newSizesFinal, newSizesAnimationStart, newSizesAnimationEnd] =
          addPanel(
            addedPanel,
            newSizesFinal ?? sizes.current,
            newSizesAnimationStart ?? sizes.current,
            newSizesAnimationEnd ?? sizes.current,
            restoreSizes.current,
            handledPanels,
            sortedResizeHandles.current.slice(0, handledPanels.length - 1),
            parentPanelWidth,
          );
      }
    }

    // Panel props have been updated (preferred or min width)
    if (!isEmpty(sizes.current)) {
      for (const [index, panel] of panelChildren.entries()) {
        const prevPanel = prevPanels.current.find(
          (prev) => prev.name === panel.name,
        );

        if (!prevPanel || panel === prevPanel) {
          break;
        }

        if (panel.preferredWidth !== prevPanel.preferredWidth) {
          const prevWidth = fractionsToPx(
            sizes.current[panel.name].value,
            parentPanelWidth,
            sortedResizeHandles.current.length,
          );

          const currentWidth =
            anyUnitToPx(
              parseValue(panel.preferredWidth) || { value: 0, unit: 'fr' },
              parentPanelWidth,
              resizeHandles.length,
            ) || 0;

          let deltaX = currentWidth - prevWidth;

          // TODO: defining the resizeHandle by the index only, does work for the Page setup, but when it comes to multiple panels that should be reconsidered
          // examples: both neighbouring panels should be shrunk/grown; the following panel is always growing; custom config ...
          const handleOrder = index > 0 ? index - 1 : index;

          // The sign defines the resize direction (which panel will be shrinked) and the outside panels shrink the main,
          // so the sidebar should be positive and the details negative.
          deltaX = index > 0 ? deltaX * -1 : deltaX;

          newSizesFinal = handleResize(
            deltaX,
            handleOrder,
            newSizesFinal,
            false,
          );
        }
      }
    }

    if (newSizesFinal !== undefined) {
      const {
        gridTemplateAreas,
        gridTemplateAreasStart,
        gridTemplateColumns,
        gridTemplateColumnsEnd,
        gridTemplateColumnsStart,
      } = getAnimationStates(
        sortedPanels.current,
        prevPanels.current,
        sortedResizeHandles.current,
        prevResizeHandles.current,
        newSizesFinal,
        newSizesAnimationStart,
        newSizesAnimationEnd,
      );

      animatePagePanels(
        gridTemplateAreas,
        gridTemplateAreasStart,
        gridTemplateColumns,
        gridTemplateColumnsStart,
        gridTemplateColumnsEnd,
        addedPanels.length,
        disableAnimations,
      );

      previousSizes.current = sizes.current;
      sizes.current = newSizesFinal;
      panelSizes.current = {
        ...convertToPanelSizes(
          newSizesFinal,
          parentPanelWidth,
          sortedResizeHandles.current.length,
        ),
      };

      return;
    }

    if (
      removedPanels.length ||
      (!isEmpty(sizes.current) && addedPanels.length)
    ) {
      // We did initialize already, a panel has only been updated, added or removed.
      // No need to re-initialize again.
      return;
    }

    // Due to some race condition, the bounding client rect width might still be 0 when
    // we want to initialize the page. By deferring the initialization for a tick, we get
    // the correct width of the parent panel and the panels can be initialized correctly.
    setTimeout(() => {
      if (
        !parentPanelRef.current ||
        // The scrollWidth is used as a root width needed for most of the divisions.
        // The panel widths can only be calculated when the scrollWidth is present as well.
        (parentPanelRef.current && parentPanelRef.current.scrollWidth === 0)
      ) {
        return;
      }

      // Initialize grid
      const { initialPanelSizes, gridTemplateStyles } =
        getInitialGridStylesAndSizes(
          parentPanelRef.current.scrollWidth,
          panelChildren,
          resizeHandles,
        );

      sizes.current = initialPanelSizes;
      panelSizes.current = {
        ...convertToPanelSizes(
          initialPanelSizes,
          parentPanelWidth,
          sortedResizeHandles.current.length,
        ),
      };
      setLayoutStyles(gridTemplateStyles);
    }, 0);
  }, [state]);

  // Callback to generate the event handlers for the resize handles.
  const generateResizeEventHandlers = useCallback(
    (handleOrder: number) => {
      const onDrag = (deltaX: number) => {
        handleResize(deltaX, handleOrder);
        window.dispatchEvent(new Event('resize')); // this triggers an update position of any open popper using the observeOverlaySize modifier
      };

      const onDragEnd = () => {
        if (!parentPanelRef.current) {
          return;
        }

        resizingHandle.current = null;
        callResizeCallbacks();
      };

      return {
        onDragStart: updateSpaceDistribution,
        onDrag,
        onDragEnd,
        onKeyDown: (deltaX: number) => handleResize(deltaX, handleOrder),
      };
    },
    // Not including:
    // - updateLayoutStyles - should not trigger an update of the callback.
    // - callResizeCallbacks - does not have any dependencies, will not update anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentPanelWidth],
  );

  const memoizedContext = useMemo(
    (): PanelsContextProps => ({
      state,
      panelSizes,
      dispatch,
      generateResizeEventHandlers,
      disableAnimations,
    }),
    [disableAnimations, generateResizeEventHandlers, state],
  );

  return (
    <KeepMountedContextProvider>
      <Grid
        data-testid={dataTestId}
        gap={0}
        ref={rootRef}
        className={clsx(
          {
            'no-sidebar': state.panels['sidebar'] === undefined,
            'no-details': state.panels['details'] === undefined,
          },
          consumerClassName,
        )}
        style={
          layoutStyles
            ? {
                position: 'relative',
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                minHeight: '0',
                gridTemplateColumns: layoutStyles.gridTemplateColumns,
                gridTemplateAreas: layoutStyles.gridTemplateAreas,
                gridTemplateRows: 'min-content 1fr',
                overflow: 'auto',
                ...consumerStyle,
              }
            : { opacity: 0 }
        }
      >
        <div ref={widthMeasurementContainer} style={{ gridArea: 'header' }} />
        <PanelsContext.Provider value={memoizedContext}>
          <ParentPanelContext.Provider
            value={{
              parentPanelWidth,
              sizes: sizes.current,
              sortedPanels: sortedPanels.current,
            }}
          >
            {children}
          </ParentPanelContext.Provider>
        </PanelsContext.Provider>
      </Grid>
    </KeepMountedContextProvider>
  );
};
